import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { FunctionComponent, useCallback, useId, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SelectedContext } from './dataQuery.types';

type ContextDisplay = {
    context: SelectedContext;
    messageId: string;
};

const DOMAIN_CONTEXTS: ContextDisplay[] = [
    {
        context: SelectedContext.TRANSPORT_ORDER,
        messageId: 'transportOrder',
    },
    {
        context: SelectedContext.TRANSPORT_CAPACITY_ORDER,
        messageId: 'transportCapacityOrder',
    },
    {
        context: SelectedContext.COMPOUND_VEHICLE,
        messageId: 'compoundVehicle',
    },
    {
        context: SelectedContext.SERVICE_OPTION,
        messageId: 'serviceOption',
    },
    {
        context: SelectedContext.SERVICE_ORDER,
        messageId: 'serviceOrder',
    },
];

const VDA_CONTEXTS: ContextDisplay[] = [
    {
        context: SelectedContext.VDA_TRANSPORT_ORDER,
        messageId: 'vdaTransportOrder',
    },
    {
        context: SelectedContext.VDA_TRANSPORT_ORDER_RESPONSE,
        messageId: 'vdaTransportOrderResponse',
    },
    {
        context: SelectedContext.VDA_TRANSPORT_STATUS_REPORTS,
        messageId: 'vdaTransportStatusReports',
    },
    {
        context: SelectedContext.VDA_COMPOUND_TRANSPORT_ORDER,
        messageId: 'vdaCompoundTransportOrder',
    },
    {
        context: SelectedContext.VDA_SERVICE_ORDER,
        messageId: 'vdaServiceOrder',
    },
    {
        context: SelectedContext.VDA_SERVICE_ORDER_RESPONSE,
        messageId: 'vdaServiceOrderResponse',
    },
    {
        context: SelectedContext.VDA_TRANSPORT_CAPACITY_ORDER,
        messageId: 'vdaTransportCapacityOrder',
    },
    {
        context: SelectedContext.VDA_TRANSPORT_CAPACITY_ORDER_RESPONSE,
        messageId: 'vdaTransportCapacityOrderResponse',
    },
];

type ContextSelectionProps = {
    disabled?: boolean;
    selectedContext: SelectedContext | undefined;
    setSelectedContext: (context: SelectedContext | undefined) => void;
};

export const ContextSelection: FunctionComponent<ContextSelectionProps> = ({
    disabled = false,
    selectedContext,
    setSelectedContext,
}) => {
    const intl = useIntl();
    const vdaContextSelectId = useId();
    const domainHeaderId = useId();
    const vdaHeaderId = useId();

    type ContextOption = SelectOption & { context?: SelectedContext | undefined };

    const onSelectionChange = useCallback(
        (option: ContextOption | undefined) => setSelectedContext(option?.context),
        [setSelectedContext],
    );

    const options = useMemo(() => {
        const toContextOption = ({ context, messageId }: ContextDisplay): ContextOption => ({
            id: context,
            context,
            label: intl.formatMessage({
                id: `outboundOrderBook.inspector.dataquery.form.context.options.${messageId}`,
            }),
        });

        return [
            {
                id: domainHeaderId,
                header: true,
                label: intl.formatMessage({ id: 'outboundOrderBook.inspector.dataquery.form.context.type.domain' }),
            },
            ...DOMAIN_CONTEXTS.map(toContextOption),
            {
                id: vdaHeaderId,
                header: true,
                label: intl.formatMessage({ id: 'outboundOrderBook.inspector.dataquery.form.context.type.vda' }),
            },
            ...VDA_CONTEXTS.map(toContextOption),
        ];
    }, [intl, domainHeaderId, vdaHeaderId]);

    return (
        <div className="form-group">
            <label htmlFor={vdaContextSelectId}>
                <FormattedMessage id="outboundOrderBook.inspector.dataquery.form.context.label" />
            </label>
            <Select
                id={vdaContextSelectId}
                dropdownClassName="max-height-400"
                placeholder={<FormattedMessage id="outboundOrderBook.inspector.dataquery.form.context.placeholder" />}
                options={options}
                disabled={disabled}
                value={selectedContext !== undefined ? [selectedContext] : undefined}
                onChange={onSelectionChange}
            />
        </div>
    );
};
